/**
 * Prompt 预览面板：把当前分镜的输入（文本 prompt + 镜头调度 + @ 角色 + 场景）
 * 实时拼装成两份"最终送给模型"的 prompt，分别给图片模型 / 视频模型。
 *
 * 真实生成时由后端拼装；本组件仅作可视化，让用户清楚镜头调度的结构化设置最终如何变成文字。
 */

import { useState } from 'react';
import { Copy, Check, Image as ImageIcon, Video } from 'lucide-react';
import {
  buildKeyframePromptPreview,
  buildVideoPromptPreview,
  type CharacterRefForPreview,
  type LayoutItemForPreview,
  type PropRefForPreview,
  type SceneRefForPreview,
} from '../../utils/directorPrompt';

interface Props {
  userPrompt: string;
  characterRefs: CharacterRefForPreview[];
  propRefs?: PropRefForPreview[];
  sceneRef?: SceneRefForPreview | null;
  characterLayout?: LayoutItemForPreview[] | null;
  cameraMovement: string | null;
  motionIntensity: string | null;
  /**
   * 仅展示「视频模型」预览（多图 → 视频模式）。
   * 此模式下没有 keyframe 生成，预览只展示送给视频模型的文本（镜头运动 + 提示词正文）。
   */
  videoOnly?: boolean;
}

type Tab = 'keyframe' | 'video';

export function PromptPreviewPanel({
  userPrompt,
  characterRefs,
  propRefs = [],
  sceneRef = null,
  characterLayout,
  cameraMovement,
  motionIntensity,
  videoOnly = false,
}: Props) {
  const [tab, setTab] = useState<Tab>(videoOnly ? 'video' : 'keyframe');
  const [copied, setCopied] = useState(false);

  const keyframePrompt = buildKeyframePromptPreview({
    userPrompt,
    characterRefs,
    propRefs,
    sceneRef,
    characterLayout,
    cameraMovement,
    motionIntensity,
  });

  const videoPrompt = buildVideoPromptPreview({
    userPrompt,
    characterNames: characterRefs.map((r) => r.name),
    propNames: propRefs.map((r) => r.name),
    sceneName: sceneRef?.name ?? null,
    cameraMovement,
    motionIntensity,
  });

  const current = videoOnly ? videoPrompt : tab === 'keyframe' ? keyframePrompt : videoPrompt;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(current);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <div className="space-y-2">
      {/* Tab 切换（多图模式下无图片预览，隐藏切换器） */}
      {!videoOnly && (
        <div className="flex items-center gap-1 rounded-2xl border border-slate-200 bg-slate-50 p-1">
          <button
            type="button"
            onClick={() => setTab('keyframe')}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-medium transition ${
              tab === 'keyframe'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <ImageIcon className="h-3.5 w-3.5" />
            图片模型
          </button>
          <button
            type="button"
            onClick={() => setTab('video')}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-medium transition ${
              tab === 'video'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <Video className="h-3.5 w-3.5" />
            视频模型
          </button>
        </div>
      )}

      {/* prompt 内容 */}
      <div className="relative rounded-2xl border border-slate-200 bg-slate-950 p-3">
        <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words pr-8 font-mono text-[11px] leading-5 text-slate-100">
          {current || <span className="text-slate-500">（空）</span>}
        </pre>
        <button
          type="button"
          onClick={handleCopy}
          disabled={!current}
          className="absolute right-2 top-2 rounded-lg bg-white/10 p-1.5 text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-40"
          title="复制"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>

      {/* 说明 */}
      <p className="text-[11px] leading-4 text-slate-500">
        {videoOnly ? (
          <>
            送给<strong className="font-semibold">视频模型</strong>激活成视频。
            画面由帧序列承载，这里只描述<strong className="font-semibold">动作 / 节奏 / 过渡</strong>，附带镜头运动。
          </>
        ) : tab === 'keyframe' ? (
          <>
            送给<strong className="font-semibold">图片模型</strong>生成关键帧。
            包含角色参考图编号、构图描述、镜头运动。
          </>
        ) : (
          <>
            送给<strong className="font-semibold">视频模型</strong>激活成视频。
            把 <span className="font-mono">@</span> 还原为角色名，附带镜头运动；
            构图信息已由首帧承载，不再重述。
          </>
        )}
      </p>
    </div>
  );
}
